import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { api, fileUrl, avatarUrl, formatApiError } from "@/lib/api";
import { Heart, MessageCircle, Bookmark, BadgeCheck, MapPin, Briefcase, GraduationCap, Languages, Utensils, Sparkles, Flag, Lock, Coins } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { countryFlag } from "@/lib/constants";

export default function ProfileDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [compat, setCompat] = useState(null);
  const [compatLoading, setCompatLoading] = useState(false);
  const [shortlisted, setShortlisted] = useState(false);
  const [reportReason, setReportReason] = useState("");
  const [showReport, setShowReport] = useState(false);
  const [unlockingContact, setUnlockingContact] = useState(false);

  const loadProfile = async () => {
    const { data } = await api.get(`/profiles/${id}`);
    setProfile(data);
  };

  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        await loadProfile();
        const sl = await api.get("/shortlist");
        setShortlisted(!!sl.data.results?.find((s) => s.user_id === id));
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
      finally { setLoading(false); }
    })();
  }, [id]);

  const sendInterest = async () => {
    try {
      await api.post("/interests/send", { to_user_id: id });
      toast.success("Interest sent");
    } catch (e) {
      const status = e.response?.status;
      const msg = formatApiError(e.response?.data?.detail);
      if (status === 402) {
        toast.error(msg || "Upgrade to Premium to send interests", {
          action: { label: "View plans", onClick: () => navigate("/plans") },
        });
      } else {
        toast.error(msg);
      }
    }
  };

  const toggleShortlist = async () => {
    try {
      if (shortlisted) { await api.delete(`/shortlist/${id}`); setShortlisted(false); toast.success("Removed"); }
      else { await api.post(`/shortlist/${id}`); setShortlisted(true); toast.success("Added"); }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const computeCompat = async () => {
    setCompatLoading(true);
    try {
      const { data } = await api.get(`/profiles/${id}/compatibility`);
      setCompat(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setCompatLoading(false); }
  };

  const submitReport = async () => {
    try { await api.post("/report", { target_user_id: id, reason: reportReason }); toast.success("Report submitted"); setShowReport(false); setReportReason(""); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const unlockContact = async () => {
    setUnlockingContact(true);
    try {
      await api.post(`/contacts/unlock/${id}`);
      toast.success("Contact unlocked");
      await refresh();
      await loadProfile();
    } catch (e) {
      const status = e.response?.status;
      const msg = formatApiError(e.response?.data?.detail);
      if (status === 402) {
        toast.error(msg, { action: { label: "Get credits", onClick: () => navigate("/plans") } });
      } else {
        toast.error(msg);
      }
    } finally { setUnlockingContact(false); }
  };

  if (loading) return <><Navbar /><div className="min-h-[60vh] flex items-center justify-center"><div className="h-10 w-10 rounded-full border-2 border-brand-gold border-t-transparent animate-spin" /></div></>;
  if (!profile) return null;
  const photo = avatarUrl(profile, 940);

  return (
    <div className="min-h-screen bg-brand-bg" data-testid="profile-detail-page">
      <Navbar />
      <div className="max-w-6xl mx-auto px-6 py-10">
        <div className="grid md:grid-cols-12 gap-8">
          <div className="md:col-span-5">
            <div className="card-elegant overflow-hidden">
              <div className="aspect-[3/4] bg-brand-muted overflow-hidden relative">
                <img src={photo} alt={profile.name} className="w-full h-full object-cover" />
                {profile.is_verified && (
                  <span className="absolute top-4 left-4 bg-white/95 text-brand-maroon text-xs font-semibold px-3 py-1 rounded-full flex items-center gap-1"><BadgeCheck className="h-3 w-3" /> Verified</span>
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 mt-4">
              <button onClick={sendInterest} className="btn-primary text-sm flex items-center justify-center gap-2" data-testid="profile-send-interest"><Heart className="h-4 w-4" /> Send Interest</button>
              <button onClick={toggleShortlist} className={`text-sm flex items-center justify-center gap-2 px-6 py-3 rounded-md font-medium transition-colors ${shortlisted ? "bg-brand-gold text-white" : "border border-brand-line text-brand-ink hover:bg-brand-muted"}`} data-testid="profile-shortlist"><Bookmark className="h-4 w-4" /> {shortlisted ? "Shortlisted" : "Shortlist"}</button>
            </div>
            <button onClick={() => navigate(`/messages?to=${profile.user_id}`)} className="btn-ghost w-full mt-3 text-sm flex items-center justify-center gap-2" data-testid="profile-message"><MessageCircle className="h-4 w-4" /> Send a message</button>
            <button onClick={() => setShowReport(!showReport)} className="text-xs text-brand-inkMute hover:text-brand-maroon mt-3 flex items-center gap-1 mx-auto"><Flag className="h-3 w-3" /> Report this profile</button>
            {showReport && (
              <div className="mt-3 p-4 border border-brand-line rounded-md bg-white">
                <textarea rows={3} value={reportReason} onChange={(e) => setReportReason(e.target.value)} placeholder="Reason..." className="input-elegant text-sm" data-testid="report-textarea" />
                <button onClick={submitReport} className="btn-primary mt-2 text-sm w-full" data-testid="report-submit">Submit</button>
              </div>
            )}
          </div>

          <div className="md:col-span-7">
            <p className="overline">{profile.community || profile.religion || "Profile"}</p>
            <h1 className="font-serif text-5xl text-brand-ink mt-2">{profile.name}</h1>
            <p className="text-brand-inkLight mt-2">
              {profile.age && `${profile.age} years`}
              {profile.height_cm && ` · ${Math.floor(profile.height_cm / 30.48)}'${Math.round((profile.height_cm / 2.54) % 12)}"`}
              {profile.marital_status && ` · ${profile.marital_status}`}
            </p>

            {/* AI Compatibility */}
            <div className="mt-6 card-elegant p-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-brand-gold" /> <span className="overline">AI Compatibility</span></div>
                {!compat && <button onClick={computeCompat} disabled={compatLoading} className="text-xs text-brand-maroon hover:underline" data-testid="compat-compute">{compatLoading ? "Analyzing..." : "Calculate"}</button>}
              </div>
              {compat && (
                <div className="mt-4">
                  <div className="flex items-end gap-3">
                    <span className="font-serif text-5xl text-brand-maroon">{compat.score}</span>
                    <span className="text-brand-inkLight pb-1">/ 100</span>
                  </div>
                  <p className="text-sm text-brand-ink mt-2">{compat.summary}</p>
                  <ul className="mt-3 space-y-1 text-xs text-brand-inkLight">
                    {compat.reasons?.map((r, i) => <li key={i} className="flex gap-2"><span className="text-brand-gold">•</span> {r}</li>)}
                  </ul>
                </div>
              )}
            </div>

            {/* About */}
            {profile.about_me && (
              <section className="mt-8">
                <p className="overline mb-2">About</p>
                <p className="text-brand-ink leading-relaxed">{profile.about_me}</p>
              </section>
            )}

            {/* Video introduction */}
            {profile.video_url && (
              <section className="mt-8">
                <p className="overline mb-2">Video introduction</p>
                <video
                  src={fileUrl(profile.video_url)}
                  controls
                  preload="metadata"
                  className="w-full rounded-lg shadow-sm bg-brand-ink/5 max-h-96"
                  data-testid="profile-detail-video"
                />
              </section>
            )}

            {/* Details */}
            <section className="mt-8 grid grid-cols-2 gap-5">
              <DetailItem icon={MapPin} label="Location" value={profile.location} />
              <DetailItem label="Country" value={profile.country ? `${countryFlag(profile.country)} ${profile.country}` : null} />
              <DetailItem icon={Briefcase} label="Profession" value={profile.profession} />
              <DetailItem icon={GraduationCap} label="Education" value={profile.education} />
              <DetailItem icon={Languages} label="Mother tongue" value={profile.mother_tongue} />
              <DetailItem label="Religion" value={profile.religion} />
              <DetailItem label="Community" value={profile.community} />
              <DetailItem label="Income" value={profile.income} />
              <DetailItem icon={Utensils} label="Diet" value={profile.diet} />
              <DetailItem label="Drinking" value={profile.drinking} />
              <DetailItem label="Smoking" value={profile.smoking} />
            </section>

            {/* Partner preferences */}
            {profile.partner_preferences && (
              <section className="mt-8">
                <p className="overline mb-3">Partner preferences</p>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  {profile.partner_preferences.age_min && <p>Age: {profile.partner_preferences.age_min} – {profile.partner_preferences.age_max}</p>}
                  {profile.partner_preferences.religion && <p>Religion: {profile.partner_preferences.religion}</p>}
                  {profile.partner_preferences.location && <p>Location: {profile.partner_preferences.location}</p>}
                </div>
              </section>
            )}

            {/* Contact / Unlock */}
            {profile.email ? (
              <div className="mt-8 p-5 bg-white border border-brand-line rounded-lg" data-testid="profile-contact-card">
                <p className="overline">Contact</p>
                <p className="mt-2 text-sm">{profile.email}</p>
                {profile.phone && <p className="text-sm">{profile.phone}</p>}
              </div>
            ) : profile.contact_locked ? (
              <div className="mt-8 p-5 bg-brand-muted rounded-lg border border-brand-gold/30" data-testid="contact-locked-card">
                <div className="flex items-start gap-3">
                  <div className="h-10 w-10 rounded-full bg-white flex items-center justify-center shrink-0 border border-brand-line">
                    <Lock className="h-4 w-4 text-brand-gold" />
                  </div>
                  <div className="flex-1">
                    <p className="font-serif text-lg text-brand-ink">Contact details locked</p>
                    <p className="text-sm text-brand-inkLight mt-1">
                      Spend 1 credit to unlock {profile.name}&apos;s phone &amp; email, or upgrade to Premium for unlimited access.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3 mt-4">
                      <button
                        onClick={unlockContact}
                        disabled={unlockingContact || Number(user?.interest_credits || 0) < 1}
                        className="btn-primary text-sm flex items-center justify-center gap-2 disabled:opacity-60"
                        data-testid="unlock-contact-btn"
                      >
                        <Coins className="h-4 w-4" /> {unlockingContact ? "Unlocking..." : "Unlock — 1 credit"}
                      </button>
                      <Link to="/plans" className="btn-gold text-sm" data-testid="upgrade-from-contact">Upgrade to Premium</Link>
                    </div>
                    <p className="text-xs text-brand-inkMute mt-3">
                      You have <span className="font-semibold text-brand-ink">{Number(user?.interest_credits || 0)}</span> credit{Number(user?.interest_credits || 0) === 1 ? "" : "s"}
                    </p>
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}

function DetailItem({ icon: Icon, label, value }) {
  if (!value) return null;
  return (
    <div className="border-l-2 border-brand-gold pl-3">
      <p className="text-[10px] uppercase tracking-[0.15em] text-brand-inkMute flex items-center gap-1">
        {Icon && <Icon className="h-3 w-3" />} {label}
      </p>
      <p className="text-sm text-brand-ink mt-0.5">{value}</p>
    </div>
  );
}
